import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  EmojiPicker,
  EmojiPickerContent,
  EmojiPickerFooter,
  EmojiPickerSearch,
} from "../emoji-picker"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Plus } from "lucide-react"
import { cn } from "@/lib/utils"

const DEFAULT_REACTIONS = [
  { emoji: "😢", label: "chat.reactions.sad" },
  { emoji: "😮", label: "chat.reactions.surprise" },
  { emoji: "😂", label: "chat.reactions.laughing" },
  { emoji: "❤️", label: "chat.reactions.heart" },
  { emoji: "👍", label: "chat.reactions.thumbsUp" },
] as const

interface ReactionSelectorProps {
  onReactionSelect?: (emoji: string | null) => void
  selectedReaction?: string
}

export function ReactionSelector({
  onReactionSelect,
  selectedReaction,
}: ReactionSelectorProps) {
  const { t } = useTranslation()
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false)

  const handleValueChange = (value: string) => {
    onReactionSelect?.(value === "" ? null : value)
  }

  const handleEmojiSelect = ({ emoji }: { emoji: string }) => {
    onReactionSelect?.(emoji === selectedReaction ? null : emoji)
    setIsEmojiPickerOpen(false)
  }

  return (
    <ToggleGroup
      data-testid="reaction-picker"
      type="single"
      value={selectedReaction}
      onValueChange={handleValueChange}
      className="flex items-center gap-0 shadow-xs"
    >
      {DEFAULT_REACTIONS.map((reaction) => (
        <ToggleGroupItem
          key={reaction.emoji}
          value={reaction.emoji}
          aria-label={t(reaction.label)}
          className="h-9 w-9 rounded-r-none border-r bg-background p-0 text-lg last:border-r-0 dark:bg-input/30"
        >
          {reaction.emoji}
        </ToggleGroupItem>
      ))}
      <Popover open={isEmojiPickerOpen} onOpenChange={setIsEmojiPickerOpen}>
        <PopoverTrigger asChild>
          <button
            onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-r-none border-r bg-background p-0 text-lg last:border-r-0 hover:bg-accent dark:bg-input/30",
              isEmojiPickerOpen && "bg-accent",
            )}
            aria-label={t("chat.reactions.more")}
          >
            <Plus className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" side="bottom" align="center">
          <EmojiPicker className="h-80" onEmojiSelect={handleEmojiSelect}>
            <EmojiPickerSearch />
            <EmojiPickerContent defaultValue={selectedReaction} />
            <EmojiPickerFooter />
          </EmojiPicker>
        </PopoverContent>
      </Popover>
    </ToggleGroup>
  )
}
